import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
  Target, Trophy, MessageSquare, Calendar, BookOpen, Users, AlertCircle, Gift,
  CheckCircle, Hourglass, Sparkles, ShieldCheck, Zap
} from 'lucide-react';

const challengeIcons = {
  messages: MessageSquare,
  victories: Trophy,
  events: Calendar,
  modules: BookOpen,
  connections: Users,
  difficulties: AlertCircle,
  congratulations: Gift,
};

export const ChallengeCard = ({ challenge, api, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [proofText, setProofText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const Icon = challengeIcons[challenge.challenge_type] || Target;
  const pending = challenge.claim_status === 'pending_coach' || challenge.claim_status === 'pending_ai';

  const submit = async () => {
    if (proofText.trim().length < 60) {
      toast.error('Raconte en 2-3 phrases ce que tu as fait (60 caractères minimum).');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/challenges/${challenge.id}/declare`, { proof_text: proofText.trim() });
      if (res.data.status === 'rejected') {
        toast.error(res.data.message);
      } else {
        toast.success(res.data.message);
        setDialogOpen(false);
        setProofText('');
      }
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      className={challenge.is_completed ? 'bg-green-500/10 border-green-500/30' : ''}
      data-testid={`challenge-card-${challenge.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
            challenge.is_completed ? 'bg-green-500/30' : 'bg-primary/20'
          }`}>
            <Icon className={`w-6 h-6 ${challenge.is_completed ? 'text-green-600' : 'text-primary'}`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold">{challenge.title}</h3>
              <Badge variant={challenge.is_completed ? 'default' : 'secondary'} className="flex-shrink-0">
                +{challenge.xp_reward} XP
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-2">{challenge.description}</p>

            <div className="flex items-center gap-2 mb-2">
              <Progress value={challenge.progress_percent} className="h-2 flex-1" />
              <span className="text-sm font-medium flex-shrink-0">
                {challenge.current_count}/{challenge.target_count}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {challenge.is_completed ? (
                <Badge className="text-xs gap-1 bg-green-500/20 text-green-700" data-testid={`challenge-done-${challenge.id}`}>
                  <CheckCircle className="w-3 h-3" />
                  Terminé · +{challenge.xp_reward} XP encaissés
                </Badge>
              ) : pending ? (
                <Badge variant="outline" className="text-xs gap-1" data-testid={`challenge-pending-${challenge.id}`}>
                  <Hourglass className="w-3 h-3" />
                  Vérification en cours
                </Badge>
              ) : challenge.verification === 'auto' ? (
                <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`challenge-auto-${challenge.id}`}>
                  <Zap className="w-3 h-3" />
                  Se valide tout seul dès que tu fais l'action
                </span>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setDialogOpen(true)}
                  data-testid={`declare-challenge-${challenge.id}`}
                >
                  <ShieldCheck className="w-4 h-4 mr-1" />
                  Valider ce défi
                </Button>
              )}
              {challenge.claim_status === 'rejected' && challenge.admin_note && (
                <span className="text-xs text-red-500">{challenge.admin_note}</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="challenge-declare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              Valider : {challenge.title}
            </DialogTitle>
            <DialogDescription>
              Ce défi vaut {challenge.xp_reward} XP et n'est pas mesurable automatiquement.
              Raconte concrètement ce que tu as fait (dates, chiffres, noms) : une analyse assistée par IA
              vérifie la cohérence, et le coach tranche en cas de doute.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Textarea
              rows={6}
              value={proofText}
              onChange={(e) => setProofText(e.target.value)}
              placeholder="Ex : J'ai appelé 5 agences de Rouen les 12 et 13 août, obtenu 2 rendez-vous de visite pour le T3 rue Jeanne d'Arc et le T2 avenue de Bretagne."
              data-testid="challenge-proof-input"
            />
            <div className="flex items-center justify-between text-xs">
              <span className={proofText.trim().length < 60 ? 'text-muted-foreground' : 'text-primary'}>
                {proofText.trim().length}/60 caractères minimum
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Sparkles className="w-3 h-3" />
                Analyse assistée par IA
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={submitting} data-testid="challenge-proof-submit">
              {submitting ? 'Vérification en cours...' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ChallengeCard;
